// --- Core ---
import {each} from "underscore";
// --- SaniQ ---
import {
  DeserializedRecord,
  deserializeItem,
  deserializeItemWithRelationships,
  Deserializer,
  deserializeSingleRelationship,
  JSONAPIRecord,
  JSONAPIReference,
  JSONAPISingleRelationship,
  OmitSerialized,
  parseAttributeValue
} from "./Helpers";
import {Patient, TmhiAdmission} from "./Patient";
import {Information} from "./Information";
import {PatientNotice} from "./patient/PatientNotice";
import {PatientSurvey} from "./patient/Survey";
import {Physician} from "./Physician";
import {deserializeMeasurement, Measurement} from "./patient/Measurement";

type AttributeOfChangeset<T extends DeserializedRecord> = Exclude<Extract<keyof OmitSerialized<T>, string>, "versions">;
type ChangesetType = null | boolean | number | string | Date;
type ChangesetValue = ChangesetType | ChangesetType[];
type VersionAuthor<T extends DeserializedRecord> = (T["additionalVersionAuthors"] extends unknown ?
  JSONAPIReference<"patient" | "physician" | "tenant_connection_mirth"> :
  JSONAPIReference<"patient" | "physician" | "tenant_connection_mirth"> | T["additionalVersionAuthors"]);
type VersionEvent = 'create' | 'update' | 'destroy';
type VersionItem = DeserializedRecord | Patient | Information | TmhiAdmission | PatientNotice | PatientSurvey | Physician | Measurement;
type VersionItemType =
  "Patient"
  | "Information"
  | "TmhiAdmission"
  | "PatientNotice"
  | "PatientSurvey"
  | "Physician"
  | "Patient::Measurement"
  | "Patient::Survey"
  | "Patient::Notice"
  | "Patient::VitalDatum";
type ChangedKeyType = 'deletedAt'
  | 'preName'
  | 'lastName'
  | 'email'
  | 'height'
  | 'weight'
  | 'birthDate'
  | 'gender'
  | 'phone'
  | 'updatedAt'
  | 'createdAt'
  | 'value'
  | string;

interface Version<T extends DeserializedRecord> extends DeserializedRecord<"version"> {
  author: VersionAuthor<T>;
  event: VersionEvent;
  item: VersionItem;
  itemType: VersionItemType
  changeset: {
    [key in AttributeOfChangeset<T>]?: [ChangesetValue, ChangesetValue];
  }
  comment?: string;
}

interface VersionData extends JSONAPIRecord {
  attributes: JSONAPIRecord["attributes"] & {
    changeset: {
      [k: string]: [number | string | null, number | string | null];
    };
  }
  relationships: {
    author: JSONAPISingleRelationship;
    item: JSONAPISingleRelationship;
  }
}

type VersionDeserializer<T extends DeserializedRecord> = Deserializer<Version<T>, VersionData>;

const deserializerByItemType = (itemType: VersionItemType) => {
  switch (itemType) {
    case "Patient::Measurement":
      return deserializeMeasurement;
    default:
      return deserializeItem;
  }
};


const deserializeVersion: VersionDeserializer<DeserializedRecord> = (data, included) => {
  //NOTE(Keune): The author may be included
  const attributes = deserializeItem<Version<DeserializedRecord>>(data);

  const author = deserializeSingleRelationship(data.relationships.author, included, deserializeItemWithRelationships) as
    VersionAuthor<DeserializedRecord>;

  const item = deserializeSingleRelationship<VersionItem>(data.relationships.item, included, deserializerByItemType(attributes.itemType));
  const changeset = {};

  each(data.attributes.changeset, (value, key) => {
    const [parsedKey, parsedValueFrom] = parseAttributeValue(value[0], key);
    const [_parsedKey, parsedValueTo] = parseAttributeValue(value[1], key);
    changeset[parsedKey] = [parsedValueFrom, parsedValueTo];
  });

  return {
    ...attributes,
    author,
    changeset,
    item
  };
};

export {
  AttributeOfChangeset,
  ChangesetValue,
  ChangedKeyType,
  deserializeVersion,
  Version,
  VersionAuthor,
  VersionDeserializer,
  VersionEvent,
  VersionItem
};
